import { addDays, t, intlLocale } from '@procumi/shared';
import { useState, type ReactNode } from 'react';
import { CardHead } from './shared';

/**
 * Următoarele 30 de zile, ca o planșă de planificare: câte un culoar pe fel
 * (livrări, termene de răspuns, material necesar, ce probabil urmează), zilele
 * pe orizontală, săptămânile marcate, azi o linie. Dedesubt, lista: fiecare
 * lucru cu data într-o plăcuță colorată pe fel. Ce e „probabil” e punctat:
 * e o estimare din ritmul de până acum, nu o dată fixă.
 */
export type HorizonKind = 'delivery' | 'deadline' | 'needed' | 'forecast';

export interface HorizonItem {
  key: string;
  kind: HorizonKind;
  /** 'AAAA-LL-ZZ' */
  day: string;
  title: string;
  detail: string;
  open?: () => void;
  action?: ReactNode;
}

const LANES: { kind: HorizonKind; label: string; one: string }[] = [
  { kind: 'delivery', label: t('Livrări'), one: t('Livrare') },
  { kind: 'deadline', label: t('Termene de răspuns'), one: t('Termen de răspuns') },
  { kind: 'needed', label: t('Material necesar'), one: t('Material necesar') },
  { kind: 'forecast', label: t('Probabil urmează'), one: t('Probabil urmează') },
];

const at = (d: string) => new Date(`${d}T12:00:00`);
const dayNum = (d: string) => Number(d.slice(8, 10));
const fmt = (d: string, o: Intl.DateTimeFormatOptions) => at(d).toLocaleDateString(intlLocale(), o);

export function Horizon({
  items,
  overdue,
  today,
  days = 30,
}: {
  items: HorizonItem[];
  overdue: HorizonItem[];
  today: string;
  days?: number;
}) {
  const [hover, setHover] = useState<string | null>(null);
  const range = Array.from({ length: days }, (_, i) => addDays(today, i));
  const col = (d: string) => range.indexOf(d);
  const pos = (i: number) => `${(i / days) * 100}%`;
  // săptămânile: de luni până duminică, prima începe azi
  const weeks: { from: number; to: number }[] = [];
  range.forEach((d, i) => {
    if (i === 0 || at(d).getDay() === 1) weeks.push({ from: i, to: i });
    else weeks[weeks.length - 1]!.to = i;
  });
  const weekEnd = addDays(today, 6);
  const groups: [string, HorizonItem[], string?][] = [
    [t('Întârziate'), overdue, 'late'],
    [t('Săptămâna asta'), items.filter((i) => i.day <= weekEnd)],
    [t('Mai încolo'), items.filter((i) => i.day > weekEnd)],
  ];

  return (
    <section className="db-card hz" aria-labelledby="hz-h">
      <CardHead
        id="hz-h"
        icon="calendar"
        tone="blue"
        title={t('Următoarele 30 de zile')}
        sub={t('Din comenzi, cereri de ofertă și necesarul de pe șantier')}
        action={
          <span className="hz-sum">
            {items.length ? t('{0} programate', items.length) : t('Nimic programat')}
            {overdue.length ? <b>{t(', {0} întârziate', overdue.length)}</b> : null}
          </span>
        }
      />

      <div className="hz-board">
        <div className="hz-weeks" aria-hidden="true">
          <span className="hz-lab" />
          <div className="hz-track">
            {weeks.map((w) => (
              <span
                key={w.from}
                className="hz-wk"
                style={{ left: pos(w.from), width: pos(w.to - w.from + 1) }}
              >
                {range[w.from]!.slice(5, 7) === range[w.to]!.slice(5, 7)
                  ? `${dayNum(range[w.from]!)}–${dayNum(range[w.to]!)} ${fmt(range[w.to]!, { month: 'short' })}`
                  : `${dayNum(range[w.from]!)} ${fmt(range[w.from]!, { month: 'short' })} – ${dayNum(range[w.to]!)} ${fmt(range[w.to]!, { month: 'short' })}`}
              </span>
            ))}
          </div>
        </div>
        {LANES.map((l) => {
          const lane = items.filter((x) => x.kind === l.kind);
          const byDay = new Map<number, HorizonItem[]>();
          for (const x of lane) {
            const i = col(x.day);
            if (i >= 0) byDay.set(i, [...(byDay.get(i) ?? []), x]);
          }
          return (
            <div key={l.kind} className={`hz-lane hz-${l.kind}`}>
              <span className="hz-lab">
                <i aria-hidden="true" />
                {t(l.label)}
                <b>{lane.length}</b>
              </span>
              <div className="hz-track">
                {weeks.slice(1).map((w) => (
                  <span key={w.from} className="hz-sep" style={{ left: pos(w.from) }} aria-hidden="true" />
                ))}
                {[...byDay].map(([i, list]) => (
                  <button
                    key={i}
                    type="button"
                    className={`hz-chip${list.some((x) => x.key === hover) ? ' on' : ''}`}
                    style={{ left: `calc(${pos(i)} + 1px)`, width: `calc(${100 / days}% - 2px)` }}
                    title={`${fmt(list[0]!.day, { weekday: 'long', day: 'numeric', month: 'long' })}\n${list.map((x) => x.title).join('\n')}`}
                    onClick={list[0]!.open}
                    onMouseEnter={() => setHover(list[0]!.key)}
                    onMouseLeave={() => setHover(null)}
                  >
                    {list.length > 1 ? list.length : ''}
                  </button>
                ))}
              </div>
            </div>
          );
        })}
        <div className="hz-now" aria-hidden="true">
          <span className="hz-lab" />
          <div className="hz-track">
            <span data-label={t('azi')} style={{ left: `calc(${pos(0)} + ${50 / days}%)` }}>
              {t('azi')}
            </span>
          </div>
        </div>
      </div>

      {items.length || overdue.length ? (
        <div className="hz-groups">
          {groups.map(([title, list, tone]) =>
            list.length ? (
              <div key={title} className={`hz-g${tone ? ` ${tone}` : ''}`}>
                <h3>{title}</h3>
                <ul>
                  {list.slice(0, 5).map((it) => (
                    <li
                      key={it.key}
                      className={`hz-${it.kind}${hover === it.key ? ' on' : ''}`}
                      onMouseEnter={() => setHover(it.key)}
                      onMouseLeave={() => setHover(null)}
                    >
                      <span
                        className="hz-date"
                        aria-label={fmt(it.day, { weekday: 'long', day: 'numeric', month: 'long' })}
                      >
                        <b>{dayNum(it.day)}</b>
                        <small>{fmt(it.day, { weekday: 'short' })}</small>
                      </span>
                      <button type="button" className="hz-t" onClick={it.open} disabled={!it.open}>
                        <span className="hz-kind">{t(LANES.find((l) => l.kind === it.kind)!.one)}</span>
                        <b>{t(it.title)}</b>
                        <span>{t(it.detail)}</span>
                      </button>
                      {it.action ?? null}
                    </li>
                  ))}
                  {list.length > 5 ? <li className="hz-more">{t('încă {0}', list.length - 5)}</li> : null}
                </ul>
              </div>
            ) : null,
          )}
        </div>
      ) : (
        <p className="hz-empty">
          {t(
            'Livrările, termenele de răspuns, materialul cerut de pe șantier și comenzile care se repetă apar aici singure.',
          )}
        </p>
      )}
    </section>
  );
}
